import { DateTime } from "luxon";
import EditIcon from "@mui/icons-material/Edit";
import { useAppSelector } from "../../../redux-store";
import {
  arrived as arrivedActivity,
  departed as departedActivity,
} from "../../../ducks/app/driver-activities";
import { useState } from "react";
import { useActions } from "../../../hooks/useActions";
import { useTranslation } from "react-i18next";
import ActivityTimeEditDialog from "./ActivityTimeEditDialog";
import {
  selectDriverArrivedAtStopFromActivities,
  selectDriverDepartedAtStopFromActivities,
} from "../../../ducks/data/route-with-stops/selectors";
import { StopNew } from "../../../ducks/data/route-with-stops/types";
import LoadingButton from "../../../components/LoadingButton";
import { useInFlightGuard } from "../../../hooks/useInFlightGuard";
import { useParams } from "../hooks";
import { parseTrailerIdParam } from "../../../routes";

type StopDriverArrivedDepartedProps = {
  buttonText: string;
  editTitle: string;
  stop: StopNew;
  date: DateTime | null;
  disabled?: boolean;
  type: "arrival" | "departure";
  action: (input: {
    stopId: string;
    cargoId: string;
    time: DateTime;
  }) => Promise<unknown>;
};

const StopDriverArrivedDeparted = (props: StopDriverArrivedDepartedProps) => {
  const { stop, disabled } = props;
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [actionInProgress, setActionInProgress] = useState(false);
  const guard = useInFlightGuard();

  const date = props.date;

  const onDateSelected = (date: DateTime) => {
    guard(() => {
      setActionInProgress(true);
      return props
        .action({
          stopId: stop.id,
          cargoId: stop.cargoId,
          time: date!,
        })
        .then(() => setShowEditDialog(false))
        .finally(() => setActionInProgress(false));
    });
  };

  const onButtonClick = () => {
    if (date) {
      setShowEditDialog(true);
    } else {
      guard(() => {
        setActionInProgress(true);
        return props
          .action({
            stopId: stop.id,
            cargoId: stop.cargoId,
            time: DateTime.now(),
          })
          .finally(() => setActionInProgress(false));
      });
    }
  };

  return (
    <>
      {showEditDialog && (
        <ActivityTimeEditDialog
          actionInProgress={actionInProgress}
          initialValue={props.date!}
          title={props.editTitle}
          onClose={() => setShowEditDialog(false)}
          onDateSelected={onDateSelected}
        />
      )}
      <LoadingButton
        loading={actionInProgress}
        fullWidth
        sx={{ minHeight: "55px", lineHeight: 1.2 }}
        disabled={disabled}
        size="large"
        variant="contained"
        onClick={onButtonClick}
        startIcon={date ? <EditIcon /> : null}
      >
        <span>{props.buttonText}</span>
      </LoadingButton>
    </>
  );
};

export const StopDriverArrived = (props: { stop: StopNew }) => {
  const { t } = useTranslation("stopPage");
  const date = useAppSelector(
    selectDriverArrivedAtStopFromActivities(props.stop.id)
  );
  const params = useParams();
  const routeId = (params.routeId as string) || "";
  const truckId = (params.truckId as string) || "";
  const unpoweredTrailerId = parseTrailerIdParam(params.trailerId as string);
  const actions = useActions({ arrivedActivity });

  return (
    <StopDriverArrivedDeparted
      {...props}
      action={(input: { stopId: string; cargoId: string; time: DateTime }) =>
        actions.arrivedActivity({
          routeId,
          truckId,
          unpoweredTrailerId,
          cargoId: input.cargoId,
          stopId: input.stopId,
          dateTime: input.time,
        })
      }
      date={date}
      type="arrival"
      buttonText={
        date
          ? `${t("arrivalDeparture.arrivedAt")}  ${date?.toFormat("HH:mm")}`
          : `${t("arrivalDeparture.arrival")}`
      }
      editTitle={t("arrivalDeparture.editArrival")}
    />
  );
};

export const StopDriverDeparted = (props: { stop: StopNew }) => {
  const { t } = useTranslation("stopPage");
  const params = useParams();
  const routeId = (params.routeId as string) || "";
  const truckId = (params.truckId as string) || "";
  const unpoweredTrailerId = parseTrailerIdParam(params.trailerId as string);
  const date = useAppSelector(
    selectDriverDepartedAtStopFromActivities(props.stop.id)
  );
  const arrivalDate = useAppSelector(
    selectDriverArrivedAtStopFromActivities(props.stop.id)
  );
  const actions = useActions({ departedActivity });

  return (
    <StopDriverArrivedDeparted
      {...props}
      action={(input: { stopId: string; cargoId: string; time: DateTime }) =>
        actions.departedActivity({
          routeId,
          cargoId: input.cargoId,
          stopId: input.stopId,
          dateTime: input.time,
          truckId,
          unpoweredTrailerId,
        })
      }
      date={date}
      type="departure"
      disabled={!arrivalDate}
      buttonText={
        date
          ? `${t("arrivalDeparture.departedAt")} ${date?.toFormat("HH:mm")}`
          : `${t("arrivalDeparture.departure")}`
      }
      editTitle={t("arrivalDeparture.editDeparture")}
    />
  );
};
